import { EventBus } from '../composables/EventBus.js';

/**
 * Debug output destinations. Shared by browser JS and workers so they do not
 * need to import the Vue `debugSettings` store.
 */
export const DEBUG_MODE_DISABLED = 'disabled';
export const DEBUG_MODE_BROWSER_CONSOLE = 'browser-console';
export const DEBUG_MODE_DS_DEBUG_PANEL = 'ds-debug-panel';
export const DEBUG_MODE_BROWSER_AND_DS_PANEL = 'browser-and-ds-panel';

/**
 * Event name emitted on EventBus when debug log entries are published for the DS panel.
 * Listen with `EventBus.on(DEBUG_LOG_EVENT, entries => { ... })`.
 */
export const DEBUG_LOG_EVENT = 'debug-log';

/**
 * Category for log entries that indicate a separator in the debug panel UI,
 * similar to how log entries are grouped in the browser console.
 */
export const LOG_CAT_UI_SEPARATOR = 'uiSeparator';

/**
 * @type {Array<{value: string, label: string}>} Options for the debug settings mode select
 */
export const DEBUG_MODE_OPTIONS = [
    { value: DEBUG_MODE_DISABLED, label: 'Disable Debugging' },
    { value: DEBUG_MODE_BROWSER_CONSOLE, label: 'In Browser Console' },
    { value: DEBUG_MODE_DS_DEBUG_PANEL, label: 'In DS Panel' },
    { value: DEBUG_MODE_BROWSER_AND_DS_PANEL, label: 'Browser Console & DS Panel' },
];

/**
 * Whether debugging is on (any mode other than disabled).
 * @param {object} settings Debug settings object
 * @returns {boolean}
 */
export const isDebugEnabled = settings => settings?.mode !== DEBUG_MODE_DISABLED && Boolean(settings?.mode);

/**
 * Whether log entries should be collected for the DS debug panel.
 * @param {object} settings Debug settings object
 * @returns {boolean}
 */
export const shouldPublishDebugLogEntriesToPanel = settings =>
    settings?.mode === DEBUG_MODE_DS_DEBUG_PANEL || settings?.mode === DEBUG_MODE_BROWSER_AND_DS_PANEL;

/**
 * Whether filtered log entries should be printed with `console.log`.
 * @param {object} settings Debug settings object
 * @returns {boolean}
 */
export const shouldPrintDebugToConsole = settings =>
    settings?.mode === DEBUG_MODE_BROWSER_CONSOLE || settings?.mode === DEBUG_MODE_BROWSER_AND_DS_PANEL;

/**
 * Whether a log entry would pass `Logger.entryPassesFilters`. This is similar
 * to `Logger.entryPassesFilters` and must be kept in sync.
 * @param {object} logEntry Log entry object
 * @param {object} settings The shared `debugSettings` store
 * @returns {boolean}
 */
export const logEntryPassesFilters = (logEntry, settings) => {
    if (settings.level < logEntry.level) {
        return false;
    }

    // prettier-ignore
    if(
        logEntry.category &&
        settings.categories.length &&
        !settings.categories.includes(logEntry.category)
    ){
        return false;
    }

    // prettier-ignore
    if(
        logEntry.playerId &&
        settings.playerIds.length &&
        !settings.playerIds.includes(logEntry.playerId)
    ){
        return false;
    }

    // prettier-ignore
    if(
        logEntry.mvpBoardTeamId &&
        settings.mvpBoardTeamIds.length &&
        !settings.mvpBoardTeamIds.includes(logEntry.mvpBoardTeamId)
    ) {
        return false;
    }

    // prettier-ignore
    if(
        logEntry.rosterSpot &&
        settings.rosterSpots.length &&
        !settings.rosterSpots.includes(logEntry.rosterSpot)
    ){
        return false;
    }

    return true;
};

/**
 * Whether this JS realm is a dedicated worker. Used so panel logs can
 * `postMessage` to the window instead of emitting on a worker-local EventBus.
 * @returns {boolean}
 */
const isDedicatedWorker = () => {
    const WorkerGlobalScopeCtor = globalThis.WorkerGlobalScope;
    return typeof WorkerGlobalScopeCtor == 'function' && self instanceof WorkerGlobalScopeCtor;
};

/**
 * Publishes debug log entries for the DS panel. On the main thread this emits
 * on EventBus. In a dedicated worker it `postMessage`s `{ logEntries }` so
 * `WebWorkerHelper` can emit on the window EventBus.
 * @param {Array<object>} entries LogEntry-shaped objects
 * @returns {void}
 */
export const publishDebugLogEntries = entries => {
    if (!entries || !entries.length) {
        return;
    }
    if (isDedicatedWorker()) {
        self.postMessage({ logEntries: entries });
        return;
    }
    EventBus.emit(DEBUG_LOG_EVENT, entries);
};

/**
 * Logs a message if the settings pass. This was written to keep it outside
 * of the scope of Vue apps because in the web worker it cannot access
 * `debugSettings`
 * @param {string|object|array} message The message to log, can be a string for
 * a normal message, an array of message to be logged using the spread operator
 * or an object for a configuration of the message. If message is an object
 * it has the following:
 * @property {array|string} message.text Either a string to log, or an
 * array that will be spread to log (so styles can be applied)
 * @property {number} [message.level] The level for the log message. Defaults
 * to 1 if not provided.
 * @property {object} [time] Configuration if logging time stuff
 * @property {string} [time.key] They key to use for time logging.
 * @property {boolean} [time.start] Whether to log using `console.time`
 * @property {boolean} [time.log] Whether to log using `console.timeLog`
 * @property {boolean} [time.end] Whether to log using `console.timeEnd`
 *
 * @param {object} settings This object matches the format of `debugSettings`
 * and is compared against `options` to see whether or not to log the message.
 *
 */
export const logIfSettingsPass = (message, settings) => {
    // settings = { Uncomment this to force show the log
    //     ...settings,
    //     mode: 'browser-console',
    //     level: 4,
    // }

    if (!isDebugEnabled(settings)) {
        return;
    }

    if (shouldPublishDebugLogEntriesToPanel(settings)) {
        const entry = messageToLogEntry(message);
        if (entry) {
            publishDebugLogEntries([entry]);
        }
    }

    if (!shouldPrintDebugToConsole(settings)) {
        return;
    }

    // --- Get lvl, default 1 but  using param if provided
    const level = typeof message.level != 'undefined' ? message.level : 1;

    // --- Skip if doesn't meet level requirements
    if (level > settings.level) {
        return;
    }

    // --- Skip if it doesn't meet category requirements
    if (typeof message.category != 'undefined' && settings.categories.length && !settings.categories.includes(message.category)) {
        return;
    }

    logMessageToConsole(message);
};

/**
 * Start timestamps for `console.time` keys in this JS realm (main thread or worker).
 * @type {Map<string, number>}
 */
const consoleTimeStarts = new Map();

/**
 * Converts a `logIfSettingsPass` message into a LogEntry-shaped object.
 * Returns `null` for `time.start` (DevTools does not print those).
 * @param {string|object|array} message
 * @returns {object|null}
 */
const messageToLogEntry = message => {
    if (message?.time?.start) {
        consoleTimeStarts.set(message.time.key, performance.now());
        return null;
    }

    const level = typeof message?.level != 'undefined' ? message.level : 1;
    const category = typeof message?.category == 'string' ? message.category : '';
    const playerId = message?.playerId || 0;
    const mvpBoardTeamId = message?.mvpBoardTeamId || 0;
    const rosterSpot = typeof message?.rosterSpot == 'string' ? message.rosterSpot : '';
    const panelContent = messageToPanelContent(message);

    const entry = {
        content: panelContent.content,
        level,
        category,
        playerId,
        mvpBoardTeamId,
        rosterSpot,
    };
    if (panelContent.contentParts?.length) {
        entry.contentParts = panelContent.contentParts;
    }
    return entry;
};

/**
 * Panel `content` and optional styled `contentParts` for a log message.
 * @param {string|object|array} message
 * @returns {{content: string, contentParts?: Array<{text: string, style: string}>}}
 */
const messageToPanelContent = message => {
    if (typeof message == 'string') {
        return { content: message };
    }

    if (Array.isArray(message)) {
        return formatArrayContent(message);
    }

    if (typeof message?.time != 'undefined') {
        return { content: formatTimePanelContent(message) };
    }

    if (Array.isArray(message?.data)) {
        return formatArrayContent(message.data);
    }

    if (typeof message?.data == 'string') {
        return { content: message.data };
    }

    if (typeof message?.data !== 'undefined') {
        return { content: stringifyForPanel(message.data) };
    }

    return { content: stringifyForPanel(message) };
};

/**
 * Formats `time.log` / `time.end` like DevTools (`key: 123.456ms`).
 * @param {object} message
 * @returns {string}
 */
const formatTimePanelContent = message => {
    const key = message.time.key;
    const start = consoleTimeStarts.get(key);
    if (message.time.end) {
        consoleTimeStarts.delete(key);
    }
    if (typeof start == 'undefined') {
        return "Timer '" + key + "' does not exist";
    }
    const elapsedMs = (performance.now() - start).toFixed(3);
    let content = key + ': ' + elapsedMs + 'ms';
    if (message.time.log && typeof message.data !== 'undefined') {
        content += ' ' + stringifyForPanel(message.data);
    }

    return content;
};

/**
 * Writes a message to the browser console (existing `logIfSettingsPass` console path).
 * @param {string|object|array} message
 * @returns {void}
 */
const logMessageToConsole = message => {
    if (typeof message == 'string') {
        console.log(message);
        return;
    }

    // --- Use custom function to handle arrays
    if (Array.isArray(message)) {
        logArray(message);
        return;
    }

    // --- If `time` key is present, then we do a console.time
    if (typeof message.time != 'undefined') {
        if (message.time.start) {
            console.time(message.time.key);
            return;
        }

        if (message.time.log) {
            console.timeLog(message.time.key, message.data);
            return;
        }

        if (message.time.end) {
            console.timeEnd(message.time.key);
            return;
        }
    }

    // --- If we're here, it's an object but not a time related one
    if (Array.isArray(message.data)) {
        logArray(message.data);
        return;
    }

    // --- If message.data is string, just log it
    if (typeof message.data == 'string') {
        console.log(message.data);
        return;
    }

    // --- If `message.data` is defined, we'll log that only, assuming
    // --- the rest of the object is log message configuration
    if (typeof message.data !== 'undefined') {
        console.log(message.data);
        return;
    }

    // --- If we're here it's probably just a regular object we want to log
    console.log(message);
};

/**
 * An array may be either a normal array we want to log or it could
 * be a combination of a string with intended styles
 * @param {*} message
 * @returns
 */
const logArray = message => {
    // --- if the message includes %c it's probably an array w/ styles so
    // --- log using the spread operator
    if (typeof message[0] === 'string' && message[0].includes('%c')) {
        console.log(...message);
        return;
    }

    // --- In this case it's probably just a normal array we want to log
    console.log(message);
};

/**
 * @param {array} message
 * @returns {{content: string, contentParts?: Array<{text: string, style: string}>}}
 */
const formatArrayContent = message => {
    if (typeof message[0] === 'string' && message[0].includes('%c')) {
        return parsePercentC(message[0], message.slice(1));
    }
    return { content: stringifyForPanel(message) };
};

/**
 * Splits a `console.log` format string on `%c` and pairs each following chunk
 * with the next CSS style argument.
 * @param {string} format
 * @param {array} styleArgs
 * @returns {{content: string, contentParts: Array<{text: string, style: string}>}}
 */
const parsePercentC = (format, styleArgs) => {
    const chunks = format.split('%c');
    const contentParts = [];
    for (let i = 0; i < chunks.length; i++) {
        const text = chunks[i];
        if (!text) {
            continue;
        }
        const style = i === 0 ? '' : styleArgs[i - 1] || '';
        contentParts.push({ text, style });
    }
    return {
        content: contentParts.map(part => part.text).join(''),
        contentParts,
    };
};

/**
 * @param {*} value
 * @returns {string}
 */
const stringifyForPanel = value => {
    if (typeof value == 'string') {
        return value;
    }
    try {
        return JSON.stringify(value);
    } catch (_err) {
        return String(value);
    }
};
